// How a survey's appearance settings (the builder's Advanced tab) turn into CSS.
// Shared by the traditional runner (SurveyPreview) and the Modern (voice) runner,
// so both read the same settings the same way.

// A new survey's look (the builder's Advanced tab starts here): bright, near
// white with a soft blue wash, navy text and buttons — the interview's day
// palette, for traditional and Modern surveys alike.
export const DEFAULT_GRADIENT = { start: "#f7faff", end: "#dcebfb" };
export const DEFAULT_SURVEY_APPEARANCE = {
    surveyColour: DEFAULT_GRADIENT,
    backgroundMode: "gradient",
    backgroundColor: "#ffffff",
    questionBackground: "#ffffff",
    questionOpacity: 0.8,
    fontColour: "#1a2b45",
    surveyTitleColor: "#00438e",
    surveyDescriptionColor: "#6b7789",
    questionFontColor: "#00438e",
    choiceFontColor: "#1c2733",
    backButtonBackgroundColor: "#ffffff",
    backButtonFontColor: "#00438e",
    nextButtonBackgroundColor: "#00438e",
    nextButtonFontColor: "#ffffff",
    submitButtonBackgroundColor: "#00438e",
    submitButtonFontColor: "#ffffff",
    completionBackgroundGradient: DEFAULT_GRADIENT,
    completionTextColor: "#00438e",
    completionCardBackgroundColor: "rgba(255, 255, 255, 0.95)",
    completionCardBackgroundColorAlpha: 0.95,
};

// A setting's colour as CSS: strings pass through, {r,g,b,a} objects become rgba().
export const rgbaToCss = (color) => {
    if (!color) return "rgba(255, 255, 255, 1)";
    if (typeof color === "string") return color;
    if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
        const alpha = color.a !== undefined ? color.a : 1;
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    }
    return "rgba(255, 255, 255, 1)";
};

// [r, g, b] of a hex (#rgb, #rrggbb) or rgb()/rgba() colour, or null.
const toRgb = (color) => {
    const css = rgbaToCss(color);
    const rgb = css.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    let hex = css.startsWith("#") ? css.slice(1) : "";
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (/^[0-9a-f]{6}$/i.test(hex)) {
        const n = parseInt(hex, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    return null;
};

// `color` at opacity `alpha`. Anything that isn't hex or rgb() (a named colour,
// "transparent") comes back unchanged.
export const withAlpha = (color, alpha) => {
    const rgb = toRgb(color);
    return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : rgbaToCss(color);
};

// `color` moved `amount` (0..1) of the way to white (amount > 0) or black (< 0).
export const shade = (color, amount) => {
    const rgb = toRgb(color);
    if (!rgb) return rgbaToCss(color);
    const target = amount > 0 ? 255 : 0;
    const k = Math.min(1, Math.abs(amount));
    const [r, g, b] = rgb.map((c) => Math.round(c + (target - c) * k));
    return `rgb(${r}, ${g}, ${b})`;
};

// The page background: one colour, the two-colour gradient, or a cover image.
export const surveyBackgroundStyle = (settings = {}) => {
    const baseStyle = { position: "relative", minHeight: "100vh", width: "100%" };
    if (settings.backgroundMode === "single") {
        return { ...baseStyle, backgroundColor: rgbaToCss(settings.backgroundColor) };
    }
    if (settings.backgroundMode === "gradient") {
        return {
            ...baseStyle,
            background: `linear-gradient(to right, ${rgbaToCss(settings.surveyColour?.start)}, ${rgbaToCss(settings.surveyColour?.end)})`,
        };
    }
    if (settings.backgroundMode === "image" && settings.backgroundImage) {
        return {
            ...baseStyle,
            backgroundImage: `url(${settings.backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
        };
    }
    return { ...baseStyle, backgroundColor: "#ffffff" };
};

// How much an image background is darkened (0..1), from its brightness setting.
export const backgroundDimming = (settings = {}) =>
    settings.backgroundMode === "image" && settings.backgroundImage
        ? Math.max(0, (100 - (settings.backgroundBrightness || 100)) / 100)
        : 0;

// A question card's fill: the question background at the question opacity.
export const questionCardBackground = (settings = {}) => withAlpha(settings.questionBackground, settings.questionOpacity || 0.2);
